import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import type { OrderDto } from "@/shared/api/types";
import { describeError } from "@/shared/api/errors";
import { useSession } from "@/shared/auth/session";
import { BillingProfileDialog } from "@/shared/components/billing-profile/BillingProfileDialog";
import { FailureCard } from "@/shared/components/FailureCard";
import { OrderDialog } from "@/shared/components/OrderDialog";
import { ROUTES } from "@/shared/constants/routes";
import { useBillingActionsPermission, useReissueOrder } from "@/shared/hooks/useBillingActions";
import { useBillingProfileStep } from "@/shared/hooks/useBillingProfileStep";
import { useBillingSummaryQuery } from "@/shared/hooks/useBillingSummary";
import { useOrgTimezone } from "@/shared/hooks/useOrgTimezone";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { replacingOf, useStaleRenewal } from "@/shared/hooks/useStaleRenewal";
import { Button } from "@/shared/ui/button";
import { formatOrgDate } from "@/shared/utils/dates";
import { payableOrder, reissuableOrder } from "@/shared/utils/orders";

/**
 * Every route under the shell renders this while the organisation is suspended: reads are refused too
 * (ADR-0009 §7). The two billing reasons have a way out — the api exempts `/billing` from the tenant-state
 * guard precisely so a suspended owner can pay (ADR-0015 §5), and AppShell lets the Subscription tab
 * through. `suspendReason` is only on `GET /billing`, which is `billing:view` (Owner), so anyone else still
 * gets the plain "contact Nexborg" message.
 *
 * "Pay now" is not always a link to an existing invoice: the renewal order expires when the grace period
 * does, so the way back is `POST /billing/orders/:id/reissue` — same lines, a fresh invoice — and paying it
 * restores the organisation with a period starting on the payment date (ADR-0015 D9).
 */
export function SuspendedInterstitial() {
  const { tenant } = useSession();
  const tz = useOrgTimezone();
  const { can } = usePermissions();
  const canManage = useBillingActionsPermission();
  const billing = useBillingSummaryQuery(can("billing", "view"));
  const reissue = useReissueOrder();
  const reissueStep = useBillingProfileStep();
  const [order, setOrder] = useState<OrderDto | null>(null);
  const staleRenewal = useStaleRenewal(billing);
  const reason = billing.data?.suspendReason ?? null;

  const kept = `Nothing has been deleted: people, devices and events are kept, and your terminals keep letting people through in the meantime.`;

  const open = payableOrder(billing.data?.openOrders);
  const expired = reissuableOrder(billing.data?.openOrders);

  /** A fresh invoice is a new order, so it can be refused for want of billing details: asked in place. */
  const reissueExpired = async (orderId: string): Promise<void> => {
    try {
      const fresh = await reissue.mutateAsync(orderId);
      reissueStep.back();
      setOrder(fresh);
    } catch (error) {
      if (reissueStep.intercept(error, () => reissueExpired(orderId))) return;
      reissueStep.back();
      const { title, detail } = describeError(error);
      toast.error(title, detail ? { description: detail } : undefined);
    }
  };

  const payNow = () => {
    if (open) {
      setOrder(open);
      return;
    }
    if (expired) void reissueExpired(expired.id);
  };

  if (reason === "payment_overdue") {
    return (
      <div className="py-12">
        <FailureCard
          title="This organisation is paused — an invoice is unpaid"
          description={`${tenant.name} is paused because its renewal was not paid before the grace period ended. ${kept} Paying starts a new period from the day you pay.`}
          hideHome
          actions={
            canManage && (open || expired) ? (
              <Button variant="primary" size="sm" loading={reissue.isPending} onClick={payNow}>
                Pay now
              </Button>
            ) : canManage ? (
              <Button asChild variant="primary" size="sm">
                <Link to={ROUTES.settingsTab("subscription")}>Open the subscription</Link>
              </Button>
            ) : null
          }
        />
        <BillingProfileDialog step={reissueStep} busy={reissue.isPending} />
        <OrderDialog
          order={order}
          onOpenChange={(isOpen) => (!isOpen ? setOrder(null) : undefined)}
          tz={tz}
          mode={billing.data?.mode ?? "razorpay"}
          replacing={replacingOf(order, staleRenewal)}
          onPaying={() => billing.watchForChange()}
        />
      </div>
    );
  }

  if (reason === "subscription_ended") {
    // The one suspension the customer asked for (M11 §14). It is not a failure and must not read as one:
    // they cancelled, the period ran out, and subscribing again is an ordinary purchase that starts a new
    // period on payment (ADR-0015 D9). The Subscription tab stays reachable while suspended (AppShell).
    const ended = billing.data?.subscription?.periodEnd ?? null;
    return (
      <div className="py-12">
        <FailureCard
          title={
            ended ? `Your subscription ended on ${formatOrgDate(ended, tz)}` : "Your subscription has ended"
          }
          description={`${tenant.name} is paused because its subscription was cancelled and the period it had been paid for has finished. ${kept} Subscribe again to continue — a new period starts the day you pay.`}
          hideHome
          actions={
            canManage ? (
              <Button asChild variant="primary" size="sm">
                <Link to={ROUTES.settingsTab("subscription")}>Subscribe again</Link>
              </Button>
            ) : null
          }
        />
      </div>
    );
  }

  if (reason === "trial_expired") {
    return (
      <div className="py-12">
        <FailureCard
          title="Your trial has ended — subscribe to continue"
          description={`${tenant.name} is paused because its trial ended with nothing bought. ${kept}`}
          hideHome
          actions={
            canManage ? (
              <Button asChild variant="primary" size="sm">
                <Link to={ROUTES.settingsTab("subscription")}>Subscribe</Link>
              </Button>
            ) : null
          }
        />
      </div>
    );
  }

  return (
    <div className="py-12">
      <FailureCard
        title="This organisation is suspended — contact Nexborg"
        description={`${tenant.name} is paused. ${kept} Access returns as soon as Nexborg lifts the suspension.`}
        hideHome
      />
    </div>
  );
}
